import { useRef } from 'react';

/**
 * Trigger the file download using a temporary anchor element
 */
function triggerDownload({ dataUri, filename }: { dataUri: string; filename: string }): void {
	const link = document.createElement('a');
	link.setAttribute('href', dataUri);
	link.setAttribute('download', filename);
	const clickEvent = new MouseEvent('click');
	link.dispatchEvent(clickEvent);
	link.remove();
}

/**
 * This hook provides asynchronous download functionality. It accepts a fetch
 * function via dependency injection.
 * It returns an action which can be used to handle the download, triggered by
 * a click event.  It prevents multiple concurrent fetches.
 *
 * @param fetchData Function that asynchronously retrieves the data to be
 * downloaded. Should return an object containing a `dataUri` property,
 * as the file download is initiated via a data URI set on an anchor
 * element.
 * @param filename The required name for the downloaded file.
 * @param onDownload Optional callback executed after the download is
 * triggered, such as for analytics or tracking purposes. Receives the
 * result of `fetchData`.
 *
 * @example
 * ```tsx
 * const { actions } = useAsyncDownload({
 *   fetchData: async () => {
 *     const result = await getPetitionQRCode();
 *     return { dataUri: result.imageBase64Uri, shareId: result.shareId };
 *   },
 *   filename: 'petition-qrcode.png',
 *   onDownload: async ({ shareId }) => {
 *     await trackDownload({ shareId });
 *   },
 * });
 *
 * // Use the download handler
 * <button onClick={actions.handleDownload}>Download</button>
 * ```
 */
export function useAsyncDownload<T extends { dataUri: string }>({
	fetchData,
	filename,
	onDownload,
}: {
	fetchData: () => Promise<T>;
	filename: string;
	onDownload?: (data: T) => Promise<void>;
}): ModelHookResult<Record<string, never>, { handleDownload: (event?: unknown) => Promise<void> }> {
	// Used to track whether the data is currently being fetched
	// so that multiple clicks only result in a single fetch.
	const isFetchingRef = useRef(false);

	const handleDownload = async (_event?: unknown) => {
		if (isFetchingRef.current) return;

		isFetchingRef.current = true;
		try {
			const result = await fetchData();
			triggerDownload({ dataUri: result.dataUri, filename });
			await onDownload?.(result);
		} finally {
			isFetchingRef.current = false;
		}
	};

	return {
		data: {},
		actions: {
			handleDownload,
		},
	};
}
